import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { MailOutline } from '@mui/icons-material';
import { getApiBaseUrl } from '../../api/config';

const PasswordResetModal = ({ open, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleRequestReset = async () => {
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const apiUrl = await getApiBaseUrl();
      const response = await fetch(`${apiUrl}/auth/password-reset/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        // Auto-close after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        setError(data.error || 'Fehler beim Anfordern des Passwort-Resets');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte überprüfen Sie die API-Verbindung.');
      console.error('Password reset request error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <MailOutline />
          Passwort zurücksetzen
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              E-Mail wurde versendet!
            </Typography>
            <Typography variant="body2">
              Falls ein Account mit dieser E-Mail-Adresse existiert, haben Sie eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts erhalten.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Bitte prüfen Sie auch Ihren Spam-Ordner.
            </Typography>
          </Alert>
        ) : (
          <Box>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Geben Sie Ihre E-Mail-Adresse ein. Sie erhalten anschließend eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts.
            </Typography>
            <TextField
              fullWidth
              label="E-Mail-Adresse"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoFocus
              disabled={loading}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && email) {
                  handleRequestReset();
                }
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              Der Reset-Link ist 1 Stunde gültig.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!success && (
          <Button
            onClick={handleRequestReset}
            variant="contained"
            disabled={loading || !email}
            startIcon={loading ? <CircularProgress size={20} /> : <MailOutline />}
          >
            {loading ? 'Sende...' : 'Reset-Link anfordern'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PasswordResetModal;
